import { useEffect, useMemo, useState } from 'react';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhClientItem,
  WazuhClientsSummaryData,
  WazuhPagedData,
} from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { applyProfileResellerScope } from '../shared/wazuhScopeParams';

export type ClientsSortParams = {
  column: string;
  sort: 'asc' | 'desc';
};

export type ClientsDataState = {
  loading: boolean;
  summaryLoading: boolean;
  summary: WazuhClientsSummaryData | null;
  items: WazuhPagedData<WazuhClientItem> | null;
};

export const useClientsData = (
  page = 0,
  pageSize = 10,
  search = '',
  sortParams: ClientsSortParams | null = null
): ClientsDataState => {
  const { scope } = useWazuhScope();
  const { userData } = useUserProfile();
  const { skipLive, wait, isPreviewMode, getPreviewState } = useEdrDataGate('clients');
  const { canWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'clients',
  );
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const profileOrgId = userData?.organization?.id;
  const [summary, setSummary] = useState<WazuhClientsSummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [items, setItems] = useState<WazuhPagedData<WazuhClientItem> | null>(null);
  const [listLoading, setListLoading] = useState(true);

  const apiScope = useMemo(
    () => applyProfileResellerScope(scope, profileOrgId),
    [scope, profileOrgId]
  );

  const trimmedSearch = search.trim();

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      const preview = getPreviewState() as { summary: WazuhClientsSummaryData; items: WazuhPagedData<WazuhClientItem> };
      setSummary(preview.summary);
      setItems(preview.items);
      setSummaryLoading(false);
      setListLoading(false);
      return undefined;
    }

    let cancelled = false;

    if (!profileOrgId) {
      setSummary(null);
      setSummaryLoading(false);
      return undefined;
    }

    if (!canSummary) {
      setSummary(null);
      setSummaryLoading(false);
      return undefined;
    }

    setSummaryLoading(true);
    wazuhAPI.dashboard
      .getClientsSummary(apiScope)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useClientsData.summary');
          setSummary(null);
        }
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiScope, isPreviewMode, profileOrgId, wait, getPreviewState, canSummary]);

  useEffect(() => {
    if (skipLive) return undefined;

    let cancelled = false;

    if (!profileOrgId) {
      setItems(null);
      setListLoading(false);
      return undefined;
    }

    if (!canList) {
      setItems(null);
      setListLoading(false);
      return undefined;
    }

    const { organizationId } = apiScope;
    const listParams = {
      ...apiScope,
      page,
      pageSize,
      ...(organizationId ? { organizationIds: organizationId } : {}),
      ...(trimmedSearch ? { search: trimmedSearch } : {}),
      ...(sortParams ? { column: sortParams.column, sort: sortParams.sort } : {}),
    };

    setListLoading(true);
    wazuhAPI.dashboard
      .getClients(listParams)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useClientsData.list');
          setItems(null);
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    apiScope,
    isPreviewMode,
    profileOrgId,
    page,
    pageSize,
    skipLive,
    trimmedSearch,
    sortParams?.column,
    sortParams?.sort,
    canList,
  ]);

  return {
    loading: listLoading,
    summaryLoading,
    summary,
    items,
  };
};
